import React, { useEffect, useState } from "react";
import { ILocations, IRegionAndState } from "../../types/locations";
import RIghtArrowIcon from "../../icons/RIghtArrowIcon";
import { getShortLocationLabel } from "../../utils/utils";

interface IProps {
  items: ILocations;
  query: string;
  selectedRegions: IRegionAndState[];
  selectedLocations: string[];
  onSelect: (selected: string[] | string, region?: IRegionAndState) => void;
  toggleRelocating: () => void;
  includeRelocating: boolean;
  setSelectedRegions: React.Dispatch<React.SetStateAction<IRegionAndState[]>>;
  searchTerm: string;
}

const LocationDropdown: React.FC<IProps> = ({
  items,
  query,
  onSelect,
  selectedRegions,
  toggleRelocating,
  includeRelocating,
  selectedLocations,
  setSelectedRegions,
  searchTerm,
}) => {
  const [activeGroup, setActiveGroup] = useState<IRegionAndState | null>(null);

  const isSearching = query !== searchTerm;

  const removeDuplicates = <T extends { value: string }>(
    items: T[]
  ): T[] => {
    return items.filter(
      (item, index, self) =>
        index === self.findIndex((i) => i.value === item.value)
    );
  };

  const handleItemClick = (itemName: string, isRegionItem?: boolean) => {
    const isSelected = selectedLocations.includes(itemName);

    const updated = isSelected
      ? selectedLocations.filter((i) => i !== itemName)
      : [...selectedLocations, itemName];

    onSelect(updated);

    if (isRegionItem) {
      const isAllSelected = activeGroup?.cities?.every((c) =>
        updated.includes(c.value)
      );

      if (isAllSelected && activeGroup) {
        setSelectedRegions((prev) => [...prev, activeGroup]);
      } else {
        setSelectedRegions((prev) =>
          prev.filter((item) => item.id !== activeGroup?.id)
        );
      }
    }
  };

  const handleRegionCheck = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    region: IRegionAndState
  ) => {
    e.stopPropagation();

    setActiveGroup(region);

    const cityValues = region.cities.map((c) => c.value);
    const isAllSelected = cityValues.every((c) =>
      selectedLocations.includes(c)
    );

    const updated = isAllSelected
      ? selectedLocations.filter((i) => !cityValues.includes(i))
      : [...new Set([...selectedLocations, ...cityValues])];

    onSelect(updated, region);
  };

  const handleRegionClick = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    region: IRegionAndState
  ) => {
    e.stopPropagation();
    setActiveGroup(region);
  };

  // Check if there are any search results
  const hasSearchResults =
    items.topCities.length > 0 ||
    items.regionAndStates.length > 0 ||
    items.internationalLocations.length > 0;

  useEffect(() => {
    if (query && items?.regionAndStates?.[0]) {
      setActiveGroup(items?.regionAndStates?.[0]);
    }
  }, [items, query]);

  useEffect(() => {
    if (!query && activeGroup) {
      setActiveGroup(null);
    }
  }, [query]);

  if (isSearching) {
    return (
      <div className={`qs_flex qs_overflow_hidden`}>
        <div className={`qs_loc_dd_container left`}>
          <div className="qs_loc_dd_list_title">Searching...</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={`qs_flex qs_overflow_hidden`}>
        <div className={`qs_loc_dd_container left`}>
          {/* No Search result  */}
          {!hasSearchResults ? (
            <div className="qs_loc_dd_list_title">No search results</div>
          ) : null}

          {/* Top Cities  */}
          {items.topCities.length > 0 && (
            <>
              <div className="qs_loc_dd_list_title">In top cities</div>
              {removeDuplicates(items.topCities).map((city) => (
                <div
                  key={city.id}
                  className="qs_loc_dd_list_item"
                  onClick={() => handleItemClick(city.value)}
                >
                  <div
                    className={`qs_loc_checkbox ${
                      selectedLocations.includes(city.value) ? "active" : ""
                    }`}
                  />
                  {getShortLocationLabel(city.value)}
                </div>
              ))}
            </>
          )}

          {/* In state and region */}
          {items.regionAndStates.length > 0 && (
            <>
              <div className="qs_loc_dd_list_title">In state and region</div>
              {items.regionAndStates.map((region) => (
                <div
                  className="qs_loc_dd_list_item"
                  onClick={(e) => handleRegionClick(e, region)}
                  key={region.id}
                >
                  <div
                    className={`qs_loc_checkbox ${
                      !!region?.cities?.length &&
                      region?.cities?.every((city) =>
                        selectedLocations.includes(city.value)
                      )
                        ? "active"
                        : ""
                    }`}
                    onClick={(e) => handleRegionCheck(e, region)}
                  />
                  {region.name} {`(${region.cities?.length})`}
                  <span className="qs_loc_dd_list_item_icon">
                    <RIghtArrowIcon />
                  </span>
                </div>
              ))}
            </>
          )}

          {/* International Locations  */}
          {items.internationalLocations.length > 0 && (
            <>
              <div className="qs_loc_dd_list_title">International Location</div>
              {removeDuplicates(items.internationalLocations).map((loc) => (
                <div
                  key={loc.id}
                  className="qs_loc_dd_list_item"
                  onClick={() => handleItemClick(loc.value)}
                >
                  <div
                    className={`qs_loc_checkbox ${
                      selectedLocations.includes(loc.value) ? "active" : ""
                    }`}
                  />
                  {getShortLocationLabel(loc.value)}
                </div>
              ))}
            </>
          )}
        </div>

        {!!activeGroup && hasSearchResults && (
          <div className={`qs_loc_dd_container right`}>
            {/* Cities  */}
            <div className="qs_loc_dd_list_title">Cities</div>
            <div className={`qs_loc_dd_list_container`}>
              {!!activeGroup?.cities?.length && (
                <div
                  className="qs_loc_dd_list_item"
                  onClick={(e) => {
                    if (activeGroup) handleRegionCheck(e, activeGroup);
                  }}
                >
                  <div
                    className={`qs_loc_checkbox ${
                      activeGroup?.cities?.every((city) =>
                        selectedLocations.includes(city.value)
                      )
                        ? "active"
                        : ""
                    }`}
                  />
                  Select all
                </div>
              )}

              {!!activeGroup?.cities?.length &&
                removeDuplicates(activeGroup.cities).map((city) => (
                  <div
                    key={city.id}
                    className="qs_loc_dd_list_item"
                    onClick={() => handleItemClick(city.value, true)}
                  >
                    <div
                      className={`qs_loc_checkbox ${
                        selectedLocations.includes(city.value) ? "active" : ""
                      }`}
                    />
                    {getShortLocationLabel(city.value)}
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>

      {/* Include relocating candidates  */}
      <div className="qs_loc_dd_relocate_toggle" onClick={toggleRelocating}>
        <div className={`qs_toggle_switch ${includeRelocating ? "on" : "off"}`}>
          <div
            className={`qs_switch_thumb ${includeRelocating ? "on" : "off"}`}
          ></div>
        </div>

        <span>Include relocating candidates</span>
      </div>
    </>
  );
};

export default LocationDropdown;
